import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

export interface HomeHeroProps {
  title: string;
  subtitle?: string;
  slides: string[];
  ctaText?: string;
  ctaLink?: string;
  institutionalText?: string;
}

const socials = [
  { icon: "ri-twitter-x-fill", label: "X", href: "https://x.com" },
  { icon: "ri-youtube-fill", label: "YouTube", href: "https://youtube.com" },
  { icon: "ri-instagram-fill", label: "Instagram", href: "https://instagram.com" },
  { icon: "ri-facebook-fill", label: "Facebook", href: "https://facebook.com" },
  { icon: "ri-linkedin-fill", label: "LinkedIn", href: "https://linkedin.com" },
];

function smoothScrollTo(targetY: number, duration = 1200) {
  const startY = window.scrollY;
  const diff = targetY - startY;
  let startTime: number | null = null;

  function easeInOutCubic(t: number) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function step(currentTime: number) {
    if (startTime === null) startTime = currentTime;
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = easeInOutCubic(progress);
    window.scrollTo(0, startY + diff * eased);
    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

export default function HomeHero({
  title,
  subtitle,
  slides,
  ctaText = "Conoce más",
  ctaLink = "#actualidad",
  institutionalText,
}: HomeHeroProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  // Auto-play
  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide, slides.length]);

  const handleCtaClick = (e: React.MouseEvent) => {
    if (ctaLink?.startsWith("#")) {
      e.preventDefault();
      const el = document.querySelector(ctaLink);
      if (el) {
        const targetY = el.getBoundingClientRect().top + window.scrollY;
        smoothScrollTo(targetY, 1400);
      }
    }
  };

  return (
    <section className="relative w-full h-[520px] sm:h-[600px] md:h-[720px] lg:h-[760px] overflow-hidden">
      {/* Background carousel */}
      {slides.map((slide, index) => (
        <img
          key={index}
          src={slide}
          alt=""
          className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ease-in-out ${
            index === currentSlide ? "opacity-100" : "opacity-0"
          }`}
          style={{ imageRendering: "-webkit-optimize-contrast" }}
        />
      ))}

      {/* Dark overlays for readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-700/60 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-cyan-600/25 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-l from-orange-500/30 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-black/10" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full w-full px-4 md:px-6 lg:px-10">
        {/* Social icons - left vertical column (hidden on small mobile) */}
        <div className="hidden md:flex absolute left-4 lg:left-10 top-1/2 -translate-y-1/2 flex-col gap-3 lg:gap-4 z-20">
          {socials.map((social) => (
            <a
              key={social.label}
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={social.label}
              className="w-10 h-10 lg:w-11 lg:h-11 rounded-full border-2 border-white/80 bg-white/10 hover:bg-white/25 hover:border-white hover:scale-110 flex items-center justify-center text-white text-base lg:text-lg transition-all duration-300 cursor-pointer backdrop-blur-sm"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={social.icon}></i>
              </div>
            </a>
          ))}
        </div>

        {/* Title */}
        <div className="text-center max-w-4xl px-2">
          {/* MásMed Logo — centered above title */}
          <img
            src="https://storage.readdy-site.link/project_files/dd803486-334c-44db-be94-f45bc3cd2f43/d8037b81-fa3c-48ec-97fe-f681366e9749_MMT-APAIZADO-BLANCO.png?v=a0e907dcb1ef384db5cf53182a5a04c1"
            alt="MásMed Tarapacá"
            className="h-10 sm:h-12 md:h-24 lg:h-28 w-auto object-contain mx-auto mb-2 sm:mb-3 md:mb-6 lg:mb-8 drop-shadow-lg"
          />

          <h1 className="font-utendo text-white text-base sm:text-lg md:text-4xl lg:text-5xl font-bold leading-snug md:leading-snug tracking-tight drop-shadow-lg">
            {title}
          </h1>

          {/* CTA Button */}
          {ctaText && (
            <a
              href={ctaLink}
              onClick={handleCtaClick}
              className="inline-flex items-center justify-center mt-4 sm:mt-6 md:mt-8 px-6 sm:px-8 md:px-10 py-2.5 sm:py-3 md:py-4 bg-primary hover:bg-primary-dark text-white text-sm md:text-base rounded-full transition-colors duration-200 shadow-lg cursor-pointer"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}
            >
              {ctaText}
              <i className="ri-arrow-right-line ml-2 text-sm"></i>
            </a>
          )}

          {/* Social icons - mobile row */}
          <div className="flex md:hidden flex-row justify-center gap-2 mt-3">
            {socials.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="w-8 h-8 rounded-full border-2 border-white/80 bg-white/10 hover:bg-white/25 hover:border-white hover:scale-110 flex items-center justify-center text-white text-sm transition-all duration-300 cursor-pointer backdrop-blur-sm"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={social.icon}></i>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom institutional block */}
      <div className="absolute bottom-3 sm:bottom-4 md:bottom-6 lg:bottom-8 left-0 right-0 z-20 px-4 md:px-6 lg:px-10">
        {/* Financing text */}
        <div className="text-center max-w-2xl mx-auto mb-2 md:mb-4">
          <p className="text-white text-[11px] sm:text-xs md:text-base leading-relaxed drop-shadow-lg"
            style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>
            {institutionalText || "Proyecto financiado por el Gobierno Regional de Tarapacá y su Consejo Regional a través del Fondo Regional para la Productividad y Desarrollo (FRPD)."}
          </p>
        </div>

        {/* Logos bar */}
        <div className="flex items-center justify-center gap-2 sm:gap-4 md:gap-10 flex-wrap">
          {/* Financia */}
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
            <span className="text-white text-[10px] sm:text-xs md:text-base uppercase tracking-wider drop-shadow-md"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>FINANCIA:</span>
            <div className="flex items-center gap-1 md:gap-2">
              <img
                src="https://storage.readdy-site.link/project_files/dd803486-334c-44db-be94-f45bc3cd2f43/6f26c859-137b-413e-b0d0-cc2dccfa176d_GORE-APAIZADO-BLANCO.png?v=fabb07f42abe3733fb8c182bec9ed063"
                alt="Gobierno Regional de Tarapacá"
                className="h-7 sm:h-8 md:h-16 lg:h-20 w-auto object-contain drop-shadow-md"
              />
            </div>
          </div>
          {/* CORE Tarapacá */}
          <div className="flex items-center gap-1 md:gap-2">
            <img
              src="https://storage.readdy-site.link/project_files/dd803486-334c-44db-be94-f45bc3cd2f43/ce295166-fa77-4f96-937c-1b07cf72d02d_CORE-APAIZADO-BLANCO.png?v=d319fb4b6e916e03c270738313aa48bb"
              alt="CORE Tarapacá"
              className="h-7 sm:h-8 md:h-16 lg:h-[72px] w-auto object-contain drop-shadow-md"
            />
          </div>
          {/* Ejecuta */}
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
            <span className="text-white text-[10px] sm:text-xs md:text-base uppercase tracking-wider drop-shadow-md"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>EJECUTA:</span>
            <div className="flex items-center gap-1 md:gap-2">
              <img
                src="https://storage.readdy-site.link/project_files/dd803486-334c-44db-be94-f45bc3cd2f43/c24cb77b-800b-4f84-b0ee-1bfaff0da8b8_UNAP-APAIZADO-BLANCO.png?v=6ba61374883a59863a9baa8030e04d98"
                alt="Universidad Arturo Prat"
                className="h-6 sm:h-7 md:h-12 w-auto object-contain drop-shadow-md"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Navigation arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center text-white/60 hover:text-white text-2xl md:text-3xl transition-colors duration-200 cursor-pointer z-30"
            aria-label="Previous slide"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center text-white/60 hover:text-white text-2xl md:text-3xl transition-colors duration-200 cursor-pointer z-30"
            aria-label="Next slide"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>

          {/* Pagination dots */}
          <div className="absolute bottom-16 sm:bottom-20 md:bottom-28 left-1/2 -translate-x-1/2 flex items-center gap-2 z-30">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                  index === currentSlide
                    ? "bg-white scale-110"
                    : "border border-white/60 bg-transparent hover:bg-white/30"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}